/** "Purchase details": who bought each car, for whom and why, answered once per car so a
 *  customs hold is answered within the hour. */
import React, { useState } from "react";
import { ChevronRight, ShieldCheck } from "lucide-react";
import { usePurchaseAnswers, useSavePurchase } from "@/services/python-api/customerHooks";
import { Card, Empty, Loading, Page, Pill } from "./parts";

const HOW = { themselves: "I bought it myself", buyers_agent: "A buyer's agent", auction_account: "On our auction account", other: "Other" };
const PURPOSE = { resale: "Resale", personal: "Personal use", fleet: "Company fleet", other: "Other" };

function Choice({ value, options, onChange }) {
  return (
    <div className="mt-1.5 flex flex-wrap gap-2">
      {Object.entries(options).map(([k, label]) => (
        <button
          type="button"
          key={k}
          onClick={() => onChange(k)}
          className={`rounded-full border px-3 py-1.5 text-[13px] ${
            value === k ? "border-[#2563EB] bg-[#EFF6FF] text-[#1D4ED8]" : "border-[#E4E7EC] text-[#344054]"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function Form({ car, onDone }) {
  const save = useSavePurchase();
  const [a, setA] = useState({
    bought_by: car.bought_by || "",
    bought_how: car.bought_how || "",
    bought_on: car.bought_on || "",
    seller: car.seller || "",
    end_user: car.end_user || "",
    purpose: car.purpose || "",
  });
  const field = (key, label, placeholder, type = "text") => (
    <label className="mt-4 block">
      <span className="text-[13px] font-medium text-[#344054]">{label}</span>
      <input
        type={type}
        value={a[key]}
        placeholder={placeholder}
        onChange={(e) => setA({ ...a, [key]: e.target.value })}
        className="mt-1.5 h-11 w-full rounded-xl border border-[#D0D5DD] px-3 text-[15px] outline-none focus:border-[#2563EB]"
      />
    </label>
  );
  return (
    <Card>
      <p className="text-[16px] font-semibold text-[#101928]">{car.car}</p>
      <p className="text-[13px] text-[#667185]">…{car.last6}</p>
      {field("bought_by", "Who physically bought it", "Full name")}
      <p className="mt-4 text-[13px] font-medium text-[#344054]">How</p>
      <Choice value={a.bought_how} options={HOW} onChange={(v) => setA({ ...a, bought_how: v })} />
      <div className="grid gap-x-3 sm:grid-cols-2">
        {field("bought_on", "Bought on", "", "date")}
        {field("seller", "Bought from", "Copart, IAA, a dealer…")}
      </div>
      {field("end_user", "Who will use it in Nigeria", "Name or company")}
      <p className="mt-4 text-[13px] font-medium text-[#344054]">What for</p>
      <Choice value={a.purpose} options={PURPOSE} onChange={(v) => setA({ ...a, purpose: v })} />
      <p className="mt-4 text-[13px] text-[#667185]">
        Add the receipt and bill of sale on the car's page under "Add a paper".
      </p>
      <div className="mt-5 flex gap-2">
        <button onClick={onDone} className="h-11 flex-1 rounded-full border border-[#D0D5DD] text-[15px] font-medium">
          Cancel
        </button>
        <button
          disabled={save.isPending}
          onClick={() => save.mutate({ last6: car.last6, ...a, bought_on: a.bought_on || null }, { onSuccess: onDone })}
          className="h-11 flex-1 rounded-full bg-[#2563EB] text-[15px] font-medium text-white disabled:opacity-60"
        >
          Save
        </button>
      </div>
    </Card>
  );
}

export default function PurchaseDetails() {
  const { data, isLoading } = usePurchaseAnswers();
  const [open, setOpen] = useState(null);
  const cars = data?.cars ?? [];
  const editing = cars.find((c) => c.last6 === open);
  return (
    <Page title="Purchase details">
      {isLoading ? (
        <Loading />
      ) : editing ? (
        <Form car={editing} onDone={() => setOpen(null)} />
      ) : cars.length === 0 ? (
        <Empty title="No cars yet" />
      ) : (
        <div className="space-y-3">
          <Card className="flex gap-3 border-[#BFDBFE] bg-[#F8FBFF]">
            <ShieldCheck size={22} color="#2563EB" className="shrink-0" />
            <p className="text-[14px] text-[#344054]">
              US Customs may ask who bought a car, for whom and why. Answer once per car and we can reply within the
              hour instead of holding your car.{" "}
              {data.missing ? <b>{data.missing} still to answer.</b> : <b>All answered.</b>}
            </p>
          </Card>
          <Card className="divide-y divide-[#F0F2F5] py-1">
            {cars.map((c) => (
              <button key={c.last6} onClick={() => setOpen(c.last6)} className="flex w-full items-center gap-3 py-3 text-left">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] font-medium text-[#101928]">{c.car}</span>
                  <span className="block text-[12px] text-[#667185]">
                    …{c.last6}
                    {c.complete ? ` · ${c.bought_by} · ${PURPOSE[c.purpose] || ""}` : ""}
                  </span>
                </span>
                <Pill tone={c.complete ? "bg-[#E7F6EC] text-[#036B26]" : "bg-[#FEF6E7] text-[#865503]"}>
                  {c.complete ? "Answered" : "To answer"}
                </Pill>
                <ChevronRight size={16} color="#98A2B3" />
              </button>
            ))}
          </Card>
        </div>
      )}
    </Page>
  );
}
